import { userSchema, usersWithAssignedRolesSchema } from "@/schemas/user.schema";
import { api as baseApi, apiJS as baseApiJS } from "@/store/api";
import { User, UserAssignedRoleKeys, UserWithAssignedRoles } from "@/types/user.types";

export const TAG_USER = "USER";
export const TAG_ADMIN_USERS_WITH_ASSIGNED_ROLES = "ADMIN_USERS_WITH_ASSIGNED_ROLES";

interface UserSettingsParams {
    userId: string;
    params: { name: string; value: any };
}

const apiJS = baseApiJS
    .enhanceEndpoints({
        addTagTypes: [TAG_USER, TAG_ADMIN_USERS_WITH_ASSIGNED_ROLES],
    })
    .injectEndpoints({
        endpoints: (builder) => ({
            getUser: builder.query<User, void>({
                query: () => ({ url: "me", method: "GET" }),
                transformResponse: (response) => {
                    const result = userSchema.safeParse(response);
                    if (result.success) {
                        return result.data as User;
                    } else {
                        console.error("Received user data in a wrong format");
                        throw (result as any).error;
                    }
                },
                providesTags: [TAG_USER],
            }),
            getAllUsersWithAssignedRoles: builder.query<UserWithAssignedRoles[], void>({
                query: () => ({ url: "admin/users", method: "GET" }),
                transformResponse: (response) => {
                    const result = usersWithAssignedRolesSchema.safeParse(response);
                    if (result.success) {
                        return result.data as UserWithAssignedRoles[];
                    } else {
                        console.error("Received users with assigned roles data in a wrong format");
                        throw (result as any).error;
                    }
                },
                providesTags: [TAG_ADMIN_USERS_WITH_ASSIGNED_ROLES],
            }),
            setUserRoles: builder.mutation<void, { userId: string; roleKeys: UserAssignedRoleKeys[] }>({
                query: ({ userId, roleKeys }) => ({
                    url: `admin/users/${userId}/roles`,
                    method: "PATCH",
                    body: { roleKeys },
                }),
                invalidatesTags: [TAG_ADMIN_USERS_WITH_ASSIGNED_ROLES],
            }),
        }),
    });

const api = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        putUserSettings: builder.mutation<void, UserSettingsParams>({
            query: ({ userId, params }) => ({
                url: `user/${userId}/setting`,
                method: "PUT",
                body: params,
            }),
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                await queryFulfilled;
                dispatch(apiJS.util.invalidateTags([TAG_USER]));
            },
        }),
    }),
});

export { apiJS as userApi };
export const { useGetUserQuery, useGetAllUsersWithAssignedRolesQuery, useSetUserRolesMutation } = apiJS;
export const { usePutUserSettingsMutation } = api;
